import React from 'react'
import { cn } from '@/lib/utils/cn'

/* ------------------------------------------------------------------ */
/*  Skeleton — shimmer loading placeholder                             */
/*  Variants: text | circle | rect                                     */
/*  Animation disabled when prefers-reduced-motion (via CSS)           */
/*  See ARCHITECTURE.md §17                                            */
/* ------------------------------------------------------------------ */

export type SkeletonVariant = 'text' | 'circle' | 'rect'

export interface SkeletonProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Shape variant */
  variant?: SkeletonVariant
  /** Width — CSS value (default: 100% for text/rect, size-dependent for circle) */
  width?: string | number
  /** Height — CSS value (default: 1em for text, auto for others) */
  height?: string | number
  /** Number of text lines to render (only used with variant="text") */
  lines?: number
}

const shimmerClass =
  'bg-neutral-200 animate-pulse motion-reduce:animate-none motion-reduce:opacity-70'

export function Skeleton({
  variant = 'text',
  width,
  height,
  lines = 1,
  className,
  style,
  ...props
}: SkeletonProps) {
  if (variant === 'text' && lines > 1) {
    return (
      <div className={cn('flex flex-col gap-2', className)} role="status" aria-label="Laddar" {...props}>
        {Array.from({ length: lines }, (_, i) => (
          <div
            key={i}
            className={cn(shimmerClass, 'rounded')}
            style={{
              width: i === lines - 1 ? '75%' : (width ?? '100%'),
              height: height ?? '1em',
              ...style,
            }}
          />
        ))}
      </div>
    )
  }

  if (variant === 'circle') {
    const size = width ?? height ?? '2.5rem'
    return (
      <div
        className={cn(shimmerClass, 'rounded-full', className)}
        role="status"
        aria-label="Laddar"
        style={{ width: size, height: size, ...style }}
        {...props}
      />
    )
  }

  // Default: text single-line or rect
  return (
    <div
      className={cn(
        shimmerClass,
        variant === 'rect' ? 'rounded-lg' : 'rounded',
        className,
      )}
      role="status"
      aria-label="Laddar"
      style={{
        width: width ?? '100%',
        height: height ?? (variant === 'text' ? '1em' : '4rem'),
        ...style,
      }}
      {...props}
    />
  )
}

Skeleton.displayName = 'Skeleton'
